/**
 * Which atlas pages the game page holds for the map it stands on (ADR-109 decisions 8 and 11, P1.4-T84): the pages the
 * manifest's `pages` name for a map, the maps it touches, and so the pages in reach. Pure data over a world manifest's
 * maps; nothing here loads or drops a texture.
 */

/**
 * The part of a manifest's map the page lists read: its size, where it is placed on the world grid in whole tiles, and the
 * indices of the asset manifest's pages its client chunks draw from. A manifest from before `pages` carries none.
 */
export interface PagedMap {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly place?: { readonly x: number; readonly y: number };
  readonly pages?: readonly number[];
}

/**
 * The ids of the placed maps that share a stretch of edge with a map or meet one of its corners (a corner where four maps
 * meet gives all four, ADR-147 decision 8). A map with no place, a map the list lacks, a gap and an overlap give none.
 *
 * @param maps The manifest's maps.
 * @param mapId The map.
 * @returns The neighbours' ids, in manifest order.
 */
export function neighbourMapIds(maps: readonly PagedMap[], mapId: string): string[] {
  const own = maps.find((map) => map.id === mapId);
  if (own?.place === undefined) {
    return [];
  }
  const from = own.place;
  const ids: string[] = [];
  for (const other of maps) {
    if (other.id === mapId || other.place === undefined) {
      continue;
    }
    const gapX = Math.max(from.x, other.place.x) - Math.min(from.x + own.width, other.place.x + other.width);
    const gapY = Math.max(from.y, other.place.y) - Math.min(from.y + own.height, other.place.y + other.height);
    if ((gapX === 0 && gapY <= 0) || (gapY === 0 && gapX <= 0)) {
      ids.push(other.id);
    }
  }
  return ids;
}

/**
 * Every page index of the asset manifest.
 *
 * @param pageCount How many pages the asset manifest has.
 * @returns 0 up to the count.
 */
function everyPage(pageCount: number): number[] {
  return Array.from({ length: pageCount }, (_, page) => page);
}

/**
 * The part of an asset manifest the animation closure reads: which page each frame is on and which frames each animation
 * plays.
 */
export interface AnimatedAtlas {
  readonly pages: readonly unknown[];
  readonly frames: { readonly [frameId: string]: { readonly page: number } | undefined };
  readonly animations: { readonly [frameId: string]: { readonly frames: readonly string[] } | undefined };
}

/**
 * Closes a set of pages over the looping animations: an animation whose first frame is on a page in the set draws all of
 * its frames, so their pages join the set, and so on for animations those start. A compiled `pages` list from before
 * P1.4-T84 lists only the pages of the frames a map draws, so the page closes it itself. The closure takes every animation
 * keyed on a listed page, whether or not the map draws it, which can only add a page.
 *
 * @param pages The page indices listed.
 * @param atlas The asset manifest.
 * @returns The page indices, ascending, each below the page count.
 */
export function closeOverAnimations(pages: readonly number[], atlas: AnimatedAtlas): number[] {
  const set = new Set(pages);
  const starts = Object.entries(atlas.animations).filter((entry): entry is [string, { readonly frames: readonly string[] }] => entry[1] !== undefined);
  let grew = true;
  while (grew) {
    grew = false;
    for (const [start, animation] of starts) {
      const page = atlas.frames[start]?.page;
      if (page === undefined || !set.has(page)) {
        continue;
      }
      for (const frame of animation.frames) {
        const framePage = atlas.frames[frame]?.page;
        if (framePage !== undefined && !set.has(framePage)) {
          set.add(framePage);
          grew = true;
        }
      }
    }
  }
  return [...set].filter((page) => page < atlas.pages.length).sort((a, b) => a - b);
}

/**
 * The pages one map draws from: those the manifest names, closed over the looping animations they start, none for a blank
 * map, and every page for a map the manifest lacks or whose entry carries no `pages` (a version from before them boots as
 * it always did).
 *
 * @param maps The manifest's maps.
 * @param mapId The map.
 * @param atlas The asset manifest.
 * @returns The page indices, ascending.
 */
export function pagesOfMap(maps: readonly PagedMap[], mapId: string, atlas: AnimatedAtlas): number[] {
  const pages = maps.find((map) => map.id === mapId)?.pages;
  return pages === undefined ? everyPage(atlas.pages.length) : closeOverAnimations(pages, atlas);
}

/**
 * The pages in reach of a map: its own and its neighbours', and those of the maps kept (the map just left, which a way back
 * through a door returns to), once each. Every page when any of those maps carries no `pages`.
 *
 * @param maps The manifest's maps.
 * @param mapId The map the page stands on.
 * @param atlas The asset manifest.
 * @param kept Further maps whose pages stay held.
 * @returns The page indices, ascending.
 */
export function pagesInReach(maps: readonly PagedMap[], mapId: string, atlas: AnimatedAtlas, kept: readonly string[] = []): number[] {
  const reach = new Set<number>();
  for (const id of [mapId, ...neighbourMapIds(maps, mapId), ...kept]) {
    for (const page of pagesOfMap(maps, id, atlas)) {
      reach.add(page);
    }
  }
  return [...reach].sort((a, b) => a - b);
}

/**
 * The held pages no map in reach uses.
 *
 * @param held The page indices the page holds.
 * @param reach The pages in reach.
 * @returns The pages to let go of, in the order held.
 */
export function pagesToDrop(held: Iterable<number>, reach: readonly number[]): number[] {
  const wanted = new Set(reach);
  return [...held].filter((page) => !wanted.has(page));
}
